import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Loader2 } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquotaCurrentQueryKey,
  getGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquotaQueryKey,
  useGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquota,
} from '@/api/generated/company-user/company-user'
import {
  usePatchApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquota,
  usePostApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquota,
} from '@/api/generated/company-management-user/company-management-user'
import { useGetApiCompaniesCurrent } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, VacationQuotaModel } from '@/api/model'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { notify } from '@/lib/notify'
import {
  RegionalHolidays,
  defaultRegionalHolidays,
  HOLIDAY_FIELDS,
  type RegionalHolidaysState,
} from './RegionalHolidays'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceModuleId: number
  employee: EmployeeUserResponse
  /** Without Employees.Edit the dialog is read-only (no create/edit). */
  canEdit: boolean
}

type WorkdaysState = {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
}

const defaultWorkdays: WorkdaysState = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
}

const WEEKDAY_KEYS: (keyof WorkdaysState)[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
]

/** Unwrap the query response from the axios wrapper if needed (hooks return the full response). */
function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

const dateOnly = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD') : '')

/** Copy a quota's holiday values into the holidays state. */
function holidaysFromQuota(quota: VacationQuotaModel): RegionalHolidaysState {
  return Object.fromEntries(
    HOLIDAY_FIELDS.map((h) => [h.key, (quota as any)[h.key] ?? defaultRegionalHolidays[h.key]]),
  ) as RegionalHolidaysState
}

/**
 * Vacation quota dialog (parasol button of the overview). Shows the active + the
 * next-year quota, allows editing existing quotas (dropdown), creating a new one
 * (if none exists) and "create next year". Ported from the old portal's
 * `VacationQuotaDialog` to React Query / shadcn. Role gating (canEdit) is still
 * missing in the new portal → editing/creating is currently always allowed.
 */
export function VacationQuotaDialog({ open, onOpenChange, serviceModuleId, employee, canEdit }: Props) {
  const { t } = useTranslation(['service-modules-absence', 'service-modules', 'common', 'filters'])
  const weekdayLabels = t('dates.weekdaysShort', { ns: 'common', returnObjects: true }) as string[]
  const queryClient = useQueryClient()
  const employeeId = employee.id ?? ''

  const [vacationDays, setVacationDays] = useState('')
  const [surplusDays, setSurplusDays] = useState('')
  const [validFrom, setValidFrom] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [surplusValidUntil, setSurplusValidUntil] = useState('')
  const [workdays, setWorkdays] = useState<WorkdaysState>(defaultWorkdays)
  const [regionalHolidays, setRegionalHolidays] = useState<RegionalHolidaysState>(defaultRegionalHolidays)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isCreateMode, setIsCreateMode] = useState(false)
  const [editQuotaId, setEditQuotaId] = useState<number | undefined>()
  const [error, setError] = useState<string | null>(null)

  // Reset the fields on every open (or employee change).
  useEffect(() => {
    if (!open) return
    setVacationDays('')
    setSurplusDays('')
    setValidFrom('')
    setValidUntil('')
    setSurplusValidUntil('')
    setWorkdays(defaultWorkdays)
    setRegionalHolidays(defaultRegionalHolidays)
    setIsEditMode(false)
    setIsCreateMode(false)
    setEditQuotaId(undefined)
    setError(null)
  }, [open, employeeId])

  const quotasQuery = useGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquota(
    serviceModuleId,
    { employeeId },
    { query: { enabled: open && !!employeeId } },
  )
  const quotas = useMemo(() => unwrap<VacationQuotaModel[]>(quotasQuery.data) ?? [], [quotasQuery.data])

  const companyQuery = useGetApiCompaniesCurrent()
  const companyId = (unwrap<{ id?: number }>(companyQuery.data))?.id ?? null

  const create = usePostApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquota()
  const update = usePatchApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquota()
  const isSubmitting = create.isPending || update.isPending

  const activeQuota = useMemo(() => {
    if (!quotas.length) return null
    const today = dayjs()
    return (
      quotas.find(
        (q) =>
          today.isAfter(dayjs(q.validFrom).subtract(1, 'day')) &&
          today.isBefore(dayjs(q.validUntil).add(1, 'day')),
      ) ?? null
    )
  }, [quotas])

  const nextYearQuota = useMemo(() => {
    if (!activeQuota) return null
    const nextYear = dayjs(activeQuota.validFrom).year() + 1
    return (
      quotas.find(
        (q) => dayjs(q.validFrom).year() === nextYear || dayjs(q.validUntil).year() === nextYear,
      ) ?? null
    )
  }, [activeQuota, quotas])

  const sortedQuotas = useMemo(
    () => [...quotas].sort((a, b) => dayjs(a.validFrom).valueOf() - dayjs(b.validFrom).valueOf()),
    [quotas],
  )

  const isCreateOnly = quotas.length === 0
  // Without edit permission the dialog stays purely informational (no form).
  // The form shows when editing, when no quota exists (yet) or
  // when the user deliberately creates an additional quota (isCreateMode).
  const showForm = canEdit && (isEditMode || isCreateOnly || isCreateMode)

  async function invalidate() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquotaQueryKey(serviceModuleId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquotaCurrentQueryKey(serviceModuleId),
      }),
    ])
  }

  /** Shared body from the form state (without companyId). */
  function buildBasePayload() {
    return {
      employeeId,
      validFrom,
      validUntil,
      totalVacationDays: Number(vacationDays),
      surplusFromPrevious: Number(surplusDays),
      surplusValidUntil,
      isMondayWorkday: workdays.monday,
      isTuesdayWorkday: workdays.tuesday,
      isWednesdayWorkday: workdays.wednesday,
      isThursdayWorkday: workdays.thursday,
      isFridayWorkday: workdays.friday,
      isSaturdayWorkday: workdays.saturday,
      isSundayWorkday: workdays.sunday,
      ...regionalHolidays,
    }
  }

  async function handleSubmit() {
    setError(null)
    if (!validFrom || !validUntil || !dayjs(validUntil).isAfter(dayjs(validFrom))) {
      setError(t('absence.quota.errors.invalidRange'))
      return
    }
    const total = Number(vacationDays)
    if (vacationDays === '' || Number.isNaN(total) || total < 0) {
      setError(t('absence.quota.errors.invalidVacationDays'))
      return
    }
    if (!surplusValidUntil) {
      setError(t('absence.quota.errors.missingSurplusUntil'))
      return
    }
    const surplus = Number(surplusDays)
    if (surplusDays === '' || Number.isNaN(surplus) || surplus < 0) {
      setError(t('absence.quota.errors.invalidSurplusDays'))
      return
    }

    try {
      if (isEditMode && editQuotaId) {
        await update.mutateAsync({
          serviceModuleId,
          data: buildBasePayload(),
          params: { vacationQuotaId: editQuotaId },
        })
      } else {
        if (companyId === null) {
          setError(t('shared.quota.companyError', { ns: 'service-modules' }))
          return
        }
        await create.mutateAsync({
          serviceModuleId,
          data: { ...buildBasePayload(), companyId },
        })
      }
      await invalidate()
      notify.success(t('absence.quota.saved'))
      onOpenChange(false)
    } catch (err) {
      notify.apiError(err, t('absence.quota.saveError'))
    }
  }

  function handleEditSelection(quota: VacationQuotaModel) {
    setVacationDays(String(quota.totalVacationDays ?? ''))
    setSurplusDays(String(quota.surplusFromPrevious ?? ''))
    setValidFrom(dateOnly(quota.validFrom))
    setValidUntil(dateOnly(quota.validUntil))
    setSurplusValidUntil(dateOnly(quota.surplusValidUntil))
    setWorkdays({
      monday: !!quota.isMondayWorkday,
      tuesday: !!quota.isTuesdayWorkday,
      wednesday: !!quota.isWednesdayWorkday,
      thursday: !!quota.isThursdayWorkday,
      friday: !!quota.isFridayWorkday,
      saturday: !!quota.isSaturdayWorkday,
      sunday: !!quota.isSundayWorkday,
    })
    setRegionalHolidays(holidaysFromQuota(quota))
    setIsEditMode(true)
    setIsCreateMode(false)
    setEditQuotaId(quota.id)
  }

  /** Open an empty form for an additional quota (not editing, not next year). */
  function handleStartCreate() {
    setVacationDays('')
    setSurplusDays('')
    setValidFrom('')
    setValidUntil('')
    setSurplusValidUntil('')
    setWorkdays(defaultWorkdays)
    setRegionalHolidays(defaultRegionalHolidays)
    setIsEditMode(false)
    setEditQuotaId(undefined)
    setIsCreateMode(true)
    setError(null)
  }

  async function handleCreateNextYear() {
    if (!activeQuota) return
    setError(null)
    if (companyId === null) {
      setError(t('shared.quota.companyError', { ns: 'service-modules' }))
      return
    }
    try {
      await create.mutateAsync({
        serviceModuleId,
        data: {
          employeeId,
          companyId,
          validFrom: dayjs(activeQuota.validFrom).add(1, 'year').format('YYYY-MM-DD'),
          validUntil: dayjs(activeQuota.validUntil).add(1, 'year').format('YYYY-MM-DD'),
          totalVacationDays: activeQuota.totalVacationDays,
          surplusFromPrevious: 0,
          surplusValidUntil: dayjs(activeQuota.surplusValidUntil).add(1, 'year').format('YYYY-MM-DD'),
          isMondayWorkday: activeQuota.isMondayWorkday,
          isTuesdayWorkday: activeQuota.isTuesdayWorkday,
          isWednesdayWorkday: activeQuota.isWednesdayWorkday,
          isThursdayWorkday: activeQuota.isThursdayWorkday,
          isFridayWorkday: activeQuota.isFridayWorkday,
          isSaturdayWorkday: activeQuota.isSaturdayWorkday,
          isSundayWorkday: activeQuota.isSundayWorkday,
          ...holidaysFromQuota(activeQuota),
        },
      })
      await invalidate()
      notify.success(t('absence.quota.nextYearCreated'))
      onOpenChange(false)
    } catch (err) {
      notify.apiError(err, t('absence.quota.nextYearError'))
    }
  }

  const employeeName = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || t('dropdown.employeeFallback', { ns: 'filters' })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('absence.quota.title', { name: employeeName })}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {quotasQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('shared.quota.loading', { ns: 'service-modules' })}
            </div>
          ) : (
            <>
              {activeQuota ? (
                <div className="flex flex-col gap-1 rounded-lg border bg-muted/30 p-3 text-sm">
                  <span className="font-semibold">{t('absence.quota.current')}</span>
                  <span>
                    {t('absence.quota.currentDays', {
                      days: activeQuota.totalVacationDays,
                      from: dayjs(activeQuota.validFrom).format('DD.MM.YYYY'),
                      until: dayjs(activeQuota.validUntil).format('DD.MM.YYYY'),
                    })}
                  </span>
                  <span className="text-muted-foreground">
                    {t('absence.quota.surplusInfo', {
                      days: activeQuota.surplusFromPrevious,
                      until: dayjs(activeQuota.surplusValidUntil).format('DD.MM.YYYY'),
                    })}
                  </span>
                  {nextYearQuota && (
                    <span className="mt-1 text-muted-foreground">
                      {t('absence.quota.nextYearInfo', {
                        days: nextYearQuota.totalVacationDays,
                        from: dayjs(nextYearQuota.validFrom).format('DD.MM.YYYY'),
                        until: dayjs(nextYearQuota.validUntil).format('DD.MM.YYYY'),
                      })}
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('absence.quota.noActive')}</p>
              )}

              {canEdit && quotas.length > 0 && (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('absence.quota.editLabel')}</Label>
                    <Select
                      value={editQuotaId != null ? String(editQuotaId) : undefined}
                      onValueChange={(v) => {
                        const quota = sortedQuotas.find((q) => q.id === Number(v))
                        if (quota) handleEditSelection(quota)
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('absence.quota.selectPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {sortedQuotas.map((quota) => (
                          <SelectItem key={quota.id} value={String(quota.id)}>
                            {dayjs(quota.validFrom).format('DD.MM.YYYY')} – {dayjs(quota.validUntil).format('DD.MM.YYYY')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleStartCreate}
                      disabled={isSubmitting || isCreateMode}
                    >
                      {t('absence.quota.createNew')}
                    </Button>
                    {activeQuota && (
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={handleCreateNextYear}
                        disabled={isSubmitting}
                      >
                        {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                        {t('absence.quota.createNextYear')}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {showForm && (
                <div className="flex flex-col gap-4 border-t pt-4">
                  <span className="text-sm font-semibold">
                    {isEditMode ? t('absence.quota.editTitle') : t('absence.quota.createTitle')}
                  </span>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="vacationDays">{t('absence.quota.vacationDays')}</Label>
                      <Input
                        id="vacationDays"
                        type="number"
                        min={0}
                        step={0.5}
                        value={vacationDays}
                        onChange={(e) => setVacationDays(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="surplusDays">{t('absence.quota.surplusDays')}</Label>
                      <Input
                        id="surplusDays"
                        type="number"
                        min={0}
                        step={0.5}
                        value={surplusDays}
                        onChange={(e) => setSurplusDays(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="validFrom">{t('absence.quota.validFrom')}</Label>
                      <Input id="validFrom" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="validUntil">{t('absence.quota.validUntil')}</Label>
                      <Input id="validUntil" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="surplusValidUntil">{t('absence.quota.surplusValidUntil')}</Label>
                    <Input
                      id="surplusValidUntil"
                      type="date"
                      value={surplusValidUntil}
                      onChange={(e) => setSurplusValidUntil(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium">{t('shared.quota.workdays', { ns: 'service-modules' })}</span>
                    <div className="flex flex-wrap gap-3">
                      {WEEKDAY_KEYS.map((key, index) => (
                        <label key={key} className="flex cursor-pointer items-center gap-1.5 text-sm">
                          <Checkbox
                            checked={workdays[key]}
                            onCheckedChange={(checked) =>
                              setWorkdays((prev) => ({ ...prev, [key]: checked === true }))
                            }
                          />
                          {weekdayLabels[index]}
                        </label>
                      ))}
                    </div>
                  </div>

                  <RegionalHolidays value={regionalHolidays} onChange={setRegionalHolidays} />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          {showForm && (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="size-4 animate-spin" />}
              {isEditMode ? t('absence.quota.saveEdit') : t('buttons.save', { ns: 'common' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
